import type { CSSProperties, RefObject } from "react";

import type {
  CdropdownAlign,
  CdropdownMode,
  CdropdownOpenDirection,
} from "./Cdropdown.types";
import { useDropdownListStyle } from "./useDropdownListStyle";
import { useDropdownMeasuredHeightRef } from "./useDropdownMeasuredHeightRef";
import { useDropdownPositionLifecycle } from "./useDropdownPositionLifecycle";
import { useDropdownPositionState } from "./useDropdownPositionState";

interface UseDropdownPositioningInput {
  isOpen: boolean;
  mode: CdropdownMode;
  anchorRect: DOMRect | null | undefined;
  triggerRef: RefObject<HTMLDivElement | null>;
  anchorRef: RefObject<HTMLElement | null> | undefined;
  align: CdropdownAlign;
  width: number | string;
  matchAnchorWidth: boolean;
  maxHeight: number | undefined;
  openDirection: CdropdownOpenDirection;
  optionsLength: number;
}

interface UseDropdownPositioningResult {
  dropdownRef: RefObject<HTMLDivElement | null>;
  dropdownListStyle: CSSProperties;
  isSurfaceMeasured: boolean;
  opensUpwards: boolean;
  onDropdownRef: (node: HTMLDivElement | null) => void;
}

export function useDropdownPositioning({
  isOpen,
  mode,
  anchorRect,
  triggerRef,
  anchorRef,
  align,
  width,
  matchAnchorWidth,
  maxHeight,
  openDirection,
  optionsLength,
}: UseDropdownPositioningInput): UseDropdownPositioningResult {
  const { dropdownRef, measuredHeight, setMeasuredHeight, onDropdownRef } =
    useDropdownMeasuredHeightRef();
  const { anchorMeasuredWidth, dropdownPosition, updateDropdownPosition } =
    useDropdownPositionState({
      mode,
      anchorRect,
      triggerRef,
      anchorRef,
      align,
      width,
      matchAnchorWidth,
      maxHeight,
      measuredHeight,
      openDirection,
      optionsLength,
    });
  useDropdownPositionLifecycle({
    isOpen,
    measuredHeight,
    setMeasuredHeight,
    updateDropdownPosition,
  });
  const dropdownListStyle = useDropdownListStyle({
    anchorMeasuredWidth,
    dropdownPosition,
    matchAnchorWidth,
    width,
  });

  return {
    dropdownRef,
    dropdownListStyle,
    isSurfaceMeasured: measuredHeight !== null,
    opensUpwards: dropdownPosition.openUpwards,
    onDropdownRef,
  };
}
